import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useState } from "react";

import { postToApp, WEBVIEW_MESSAGE_TYPES } from "@/shared/webview-bridge.constants";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackUserEvent } from "@/analytics/tracking";

import { useCurrentLocale } from "@/context/LanguageContext";

import ActionButton from "@/components/buttons/action-button";
import { KINDS, SIZES } from "@/components/buttons/buttons.constants";

import { useInAppDarkMode } from "@/hooks/useInAppDarkMode";

import styles from "./gift-card-order-delayed.module.scss";

type Props = {
    imageUrl: string | null;
    sku: string;
    isGift: boolean;
};

/*
 * In-app "still processing" notice for the gift card checkout webview. Shown as a
 * terminal state when the order is still processing after five minutes of polling
 * (see GC_ORDER_STATUS_DELAYED_NOTICE_TIMEOUT) — the supplier (e.g. Blackhawk) is
 * slow but the order has NOT failed. Rather than hold the customer on an indefinite
 * spinner or a false error screen — both of which drove duplicate orders — we tell
 * them it's processing and to avoid re-ordering.
 *
 * Modelled on GiftCardOrderConfirmed: same webview shell + gift card art tile and a
 * single "Done" close CTA for both self and gift (the card isn't ready to view yet).
 */
export const GiftCardOrderDelayed: React.FC<Props> = ({ imageUrl, sku, isGift }) => {
    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const router = useRouter();
    const isDark = useInAppDarkMode();
    const [imageFailed, setImageFailed] = useState(false);

    const showImage = Boolean(imageUrl) && !imageFailed;
    const headerKey = isGift
        ? "giftCards.orderDelayed.giftHeader"
        : "giftCards.orderDelayed.header";
    const messageKey = isGift
        ? "giftCards.orderDelayed.giftMessage"
        : "giftCards.orderDelayed.message";

    const onDone = () => {
        trackUserEvent(
            locale,
            router.asPath,
            UI_CONTEXTS.GIFTCARD_ORDER_CONFIRMATION,
            EVENTS.GIFT_CARD_ORDER_DELAYED_DONE_CLICK,
            { sku }
        );
        postToApp(WEBVIEW_MESSAGE_TYPES.ON_GIFT_CARD_CLOSE);
    };

    return (
        <div className={classNames(styles.container, { [styles.dark]: isDark })}>
            <div className={styles.cardArtFrame}>
                <div className={styles.cardTile}>
                    {showImage && (
                        <img
                            className={styles.cardImage}
                            src={imageUrl}
                            alt=""
                            onError={() => setImageFailed(true)}
                        />
                    )}
                </div>
            </div>
            <h2 className={styles.header}>{t(headerKey)}</h2>
            <p className={styles.message}>
                {t(messageKey)}{" "}
                <em className={styles.warning}>{t("giftCards.orderDelayed.warning")}</em>
            </p>
            <div className={styles.ctaContainer}>
                <div className={styles.doneButton}>
                    <ActionButton
                        disabled={false}
                        fluid={true}
                        forceArcade={true}
                        kind={KINDS.BLACK}
                        label={t("giftCards.orderDelayed.done")}
                        onClick={onDone}
                        size={SIZES.LARGE}
                    />
                </div>
            </div>
        </div>
    );
};
